/**
 * Pipelines API Service
 *
 * Cliente API para pipelines y ML insights
 */

import { apiClient } from '@/lib/api-client';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Generic paginated response from Django REST Framework
 */
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface PipelineStage {
  id: string;
  pipeline: string;
  company: string;
  name: string;
  description: string;
  semantic_id: string;
  semantic_category: string;
  order_position: number;
  color: string;
  icon: string;
  manual_probability: number;
  ml_probability: number | null;
  ml_enabled: boolean;
  expected_duration_min_days: number | null;
  expected_duration_max_days: number | null;
  expected_duration_avg_days: number | null;
  is_editable: boolean;
  stage_config?: {
    sla?: { max_hours?: number; warning_percent?: number; escalate_to?: string };
    rules?: { is_reversible?: boolean; min_contact_attempts?: number; requires_reason_on_regress?: boolean };
    trigger?: { auto_events?: string[]; auto_percent?: number };
  };
  created_at: string;
  updated_at: string;
}

/**
 * Stage funnel statistics for a single stage
 */
export type ZoneGroup = 'prospection' | 'presentation' | 'negotiation' | 'closing';

export interface StageFunnelStat {
  id: string;
  name: string;
  order_position: number;
  color: string;
  semantic_id: string;
  zone_group: ZoneGroup;
  current_count: number;      // Leads currently in this stage
  historical_count: number;   // Total leads that entered this stage (funnel)
  total_value: number;        // Sum of budget_max for leads in this stage
  conversion_rate: number;    // % of first stage leads that reached here
  step_conversion_rate: number; // % conversion from previous stage
  avg_days_in_stage: number | null; // Average days leads spend in this stage
  manual_probability: number; // Configured probability for this stage
  expected_duration_avg_days: number | null;
  actions_count: number;      // Total tasks (StageActions) in this stage
  automations_count: number;  // Total automations linked to this stage
  cancelled_count: number;    // Leads cancelled from this stage
}

/**
 * Zone summary — 4 macro zones for director-level reporting
 */
export interface ZoneFunnelStat {
  zone_group: ZoneGroup;
  label: string;
  color: string;
  current_count: number;
  historical_count: number;
  total_value: number;
  cancelled_count: number;
  avg_days: number | null;
  conversion_rate: number;
  stages: string[];         // semantic_ids of stages in this zone
  stage_count: number;
}

/**
 * Pipeline funnel statistics response
 */
export interface PipelineFunnelStatsResponse {
  pipeline_id: string;
  pipeline_name: string;
  total_leads: number;           // Total leads currently active in pipeline
  total_funnel_leads: number;    // Total leads that entered first stage (funnel base)
  stages: StageFunnelStat[];
  zones: ZoneFunnelStat[];       // 4 macro zones for director-level view
}

/**
 * PipelineConfiguration - FULL version (used in GET detail)
 * Includes nested stages, zones, scores, etc.
 */
export interface PipelineConfiguration {
  id: string;
  company: string;
  project: string | null;
  name: string;
  description: string;
  type: 'lead' | 'deal' | 'project' | 'custom' | 'client';
  is_active: boolean;
  stages: PipelineStage[];
  created_at: string;
  updated_at: string;
}

/**
 * PipelineConfigurationList - LIGHTWEIGHT version (used in LIST)
 * Does NOT include stages array - only metadata
 */
export interface PipelineConfigurationList {
  id: string;
  name: string;
  type: 'lead' | 'deal' | 'project' | 'custom' | 'client';
  pipeline_type: string;
  version: number;
  semantic_version: string;
  ml_enabled: boolean;
  is_active: boolean;
  is_published: boolean;
  total_stages: number; // Count only, not full array
  leads_count: number;
  created_at: string;
  updated_at: string;
  is_locked: boolean;
  locked_at: string | null;
  locked_reason: string | null;
  active_leads_count: number;
}

export interface PipelineListParams {
  company?: string;
  project?: string;
  type?: 'lead' | 'deal' | 'project' | 'custom' | 'client';
  is_active?: boolean;
}

// ============================================================================
// PIPELINE OVERVIEW TYPES
// ============================================================================

export interface PipelineOverviewStage {
  id: string;
  name: string;
  semantic_id: string;
  order_position: number;
  leads_count: number;
  total_value: number;
  avg_duration_days: number;
  avg_duration_change: number;
  duration_trend: 'up' | 'down' | 'stable';
  health_status: 'healthy' | 'warning' | 'critical';
  conversion_rate: number;
  conversion_rate_change: number;
  conversion_trend: 'up' | 'down' | 'stable';
  loss_rate: number;
  probability: {
    value: number;
    source: string;
    confidence_level: number;
    sample_size: number;
  };
  stage_analytics: {
    processed_last_90d: number;
    won_last_90d: number;
    lost_last_90d: number;
    still_active: number;
    total_value_in_stage: number;
  };
  zone?: {
    id: string | null;
    name: string | null;
    color: string | null;
  } | null;
}

export interface PipelineOverviewResponse {
  pipeline: {
    id: string;
    name: string;
    total_leads: number;
    total_value: number;
    progress: {
      weighted_stage_index: number;
      percentage: number;
      dominant_stage: {
        id: string;
        name: string;
        leads_count: number;
      } | null;
    };
  };
  stages: PipelineOverviewStage[];
  insights: Array<{
    type: 'bottleneck' | 'alert' | 'opportunity';
    severity: 'high' | 'medium' | 'low';
    title: string;
    description: string;
    affected_leads: number;
    stage_id: string;
  }>;
  metrics: {
    conversion_rate: number;
    conversion_rate_change: number;
    conversion_trend: 'up' | 'down' | 'stable';
    avg_deal_time: number;
    avg_deal_time_change: number;
    deal_time_trend: 'up' | 'down' | 'stable';
    active_leads: number;
    active_leads_change: number;
    total_pipeline_value: number;
    total_pipeline_value_change: number;
  };
}

export interface PipelineMLInsight {
  id: string;
  company: string;
  pipeline: string;

  // Classification
  insight_type: 'recommendation' | 'anomaly' | 'opportunity' | 'warning';
  category: string;
  severity: 'low' | 'medium' | 'high' | 'critical';

  // Content
  title: string;
  description: string;

  // ML Data
  confidence_score: number; // 0-100
  data: Record<string, any>; // Backend uses 'data' not 'evidence'

  // Recommendations
  recommendation: string;
  expected_impact: Record<string, any>;

  // Relations
  related_stages: string[]; // Stage IDs
  related_stages_detail?: Array<{
    id: string;
    name: string;
    zone_name: string;
  }>;
  related_playbooks: string[]; // Playbook IDs
  related_playbooks_detail?: Array<{
    id: string;
    code: string;
    name: string;
  }>;

  // Tracking
  status: 'new' | 'reviewed' | 'applied' | 'dismissed';
  generated_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  reviewed_by_detail?: {
    id: string;
    full_name: string;
    email: string;
  };
  applied_at: string | null;
  applied_by: string | null;

  // Timestamps
  created_at: string;
  updated_at: string;
}

export interface PipelineMLInsightListParams {
  pipeline?: string;
  insight_type?: 'recommendation' | 'anomaly' | 'opportunity' | 'warning';
  severity?: 'low' | 'medium' | 'high' | 'critical';
  status?: 'new' | 'reviewed' | 'applied' | 'dismissed';
  limit?: number;
  offset?: number;
}

// ============================================================================
// PIPELINES API
// ============================================================================

export const pipelinesConfigApi = {
  /**
   * List pipeline configurations (LIGHTWEIGHT - no stages array)
   * GET /api/pipelines/configurations/
   *
   * Returns paginated response with lightweight serializer.
   * Use get() to retrieve full pipeline with stages.
   *
   * @returns Paginated response with pipeline metadata only
   */
  async list(params?: PipelineListParams): Promise<PaginatedResponse<PipelineConfigurationList>> {
    const response = await apiClient.get<PaginatedResponse<PipelineConfigurationList>>(
      '/pipelines/configurations/',
      { params }
    );
    return response;
  },

  /**
   * Create a new pipeline configuration
   * POST /api/pipelines/configurations/
   *
   * @param data - Pipeline data (name, description, project, type)
   * @returns Created pipeline configuration
   */
  async create(data: {
    name: string;
    description?: string;
    project: string;
    type: 'lead' | 'client';
  }): Promise<PipelineConfiguration> {
    return await apiClient.post<PipelineConfiguration>('/pipelines/configurations/', data);
  },

  /**
   * Get pipeline configuration by ID (FULL - includes stages)
   * GET /api/pipelines/configurations/{id}/
   *
   * Returns full pipeline with nested stages, zones, scores, etc.
   *
   * @param id - Pipeline UUID
   * @returns Full pipeline configuration with stages array
   */
  async get(id: string): Promise<PipelineConfiguration> {
    return await apiClient.get<PipelineConfiguration>(`/pipelines/configurations/${id}/`);
  },

  /**
   * Get stages for a specific pipeline
   * Returns stages ordered by order_position
   *
   * @param pipelineId - Pipeline UUID
   * @returns Array of stages sorted by order
   */
  async getStages(pipelineId: string): Promise<PipelineStage[]> {
    const pipeline = await this.get(pipelineId);
    return pipeline.stages || [];
  },

  /**
   * Apply a pipeline template to create all stages at once
   * POST /api/pipelines/configurations/{id}/apply_template/
   */
  async applyTemplate(pipelineId: string, templateCode: string): Promise<ApplyPipelineTemplateResponse> {
    return await apiClient.post<ApplyPipelineTemplateResponse>(
      `/pipelines/configurations/${pipelineId}/apply_template/`,
      { template_code: templateCode }
    );
  },

  /**
   * Get pipeline overview with complete metrics
   * GET /api/pipelines/configurations/overview/
   *
   * Returns comprehensive pipeline data including:
   * - Total leads and value
   * - Stage breakdown with real metrics (leads_count, total_value, avg_duration_days, conversion_rate)
   * - Pipeline-wide metrics (conversion_rate, avg_deal_time)
   * - Insights and alerts
   *
   * @param params.type - Pipeline type ('lead' or 'client'), defaults to 'lead'
   * @param params.projectId - Optional project ID to filter by specific project
   * @returns Complete pipeline overview with all metrics
   */
  async getOverview(params?: { type?: 'lead' | 'client'; projectId?: string }): Promise<PipelineOverviewResponse> {
    return await apiClient.get<PipelineOverviewResponse>(
      '/pipelines/configurations/overview/',
      { params: { type: params?.type || 'lead', project_id: params?.projectId } }
    );
  },
};

// ============================================================================
// PIPELINE STAGES API
// ============================================================================

export interface CreatePipelineStageRequest {
  pipeline: string;  // Pipeline UUID
  name: string;
  order_position: number;
  description?: string;
  color?: string;
  icon?: string;
  semantic_id?: string;
  semantic_category?: string;
  manual_probability?: number;
  ml_enabled?: boolean;
  expected_duration_min_days?: number;
  expected_duration_max_days?: number;
  expected_duration_avg_days?: number;
}

export interface UpdatePipelineStageRequest {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  order_position?: number;
  semantic_id?: string;
  semantic_category?: string;
  manual_probability?: number;
  ml_enabled?: boolean;
  expected_duration_min_days?: number;
  expected_duration_max_days?: number;
  expected_duration_avg_days?: number;
}

export const pipelineStagesApi = {
  /**
   * List all stages (optionally filtered by pipeline)
   * GET /api/pipelines/stages/
   */
  async list(params?: { pipeline?: string }): Promise<PaginatedResponse<PipelineStage>> {
    return await apiClient.get<PaginatedResponse<PipelineStage>>(
      '/pipelines/stages/',
      { params }
    );
  },

  /**
   * Get a single stage by ID
   * GET /api/pipelines/stages/{id}/
   */
  async get(id: string): Promise<PipelineStage> {
    return await apiClient.get<PipelineStage>(`/pipelines/stages/${id}/`);
  },

  /**
   * Create a new stage
   * POST /api/pipelines/stages/
   *
   * @param data - Stage data (pipeline, name, order_position are required)
   * @returns Created stage
   */
  async create(data: CreatePipelineStageRequest): Promise<PipelineStage> {
    return await apiClient.post<PipelineStage>('/pipelines/stages/', data);
  },

  /**
   * Update a stage
   * PATCH /api/pipelines/stages/{id}/
   */
  async update(id: string, data: UpdatePipelineStageRequest): Promise<PipelineStage> {
    return await apiClient.patch<PipelineStage>(`/pipelines/stages/${id}/`, data);
  },

  /**
   * Delete a stage (soft delete - archives it)
   * DELETE /api/pipelines/stages/{id}/
   */
  async delete(id: string): Promise<void> {
    return await apiClient.delete(`/pipelines/stages/${id}/`);
  },

  /**
   * Reorder stages in a pipeline
   * POST /api/pipelines/configurations/{pipelineId}/reorder-stages/
   */
  async reorder(pipelineId: string, stageIds: string[]): Promise<void> {
    return await apiClient.post(`/pipelines/configurations/${pipelineId}/reorder-stages/`, {
      stage_ids: stageIds,
    });
  },

  /**
   * Get stage templates/suggestions
   * GET /api/pipelines/stages/templates/?type=lead|client
   *
   * Returns predefined stage examples that can be used as templates
   * when creating new stages.
   */
  async getTemplates(type: 'lead' | 'client' = 'lead'): Promise<StageTemplatesResponse> {
    return await apiClient.get<StageTemplatesResponse>('/pipelines/stages/templates/', {
      params: { type },
    });
  },

  /**
   * Bulk create all stages from playbook
   * POST /api/pipelines/stages/bulk-create-from-playbook/
   *
   * Creates all stages defined in the playbook for the given pipeline.
   * Optionally creates all tasks for each stage too.
   */
  async bulkCreateFromPlaybook(params: {
    pipeline_id: string;
    type: 'lead' | 'client';
    include_tasks?: boolean;
  }): Promise<BulkCreateStagesFromPlaybookResponse> {
    return await apiClient.post<BulkCreateStagesFromPlaybookResponse>(
      '/pipelines/stages/bulk-create-from-playbook/',
      params
    );
  },
};

// Stage template type
export interface StageTemplate {
  semantic_id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  order_position: number;
  manual_probability: number;
  semantic_category: string;
  expected_duration_min_days: number;
  expected_duration_max_days: number;
  expected_duration_avg_days: number;
}

export interface StageTemplatesResponse {
  type: 'lead' | 'client';
  count: number;
  templates: StageTemplate[];
}

export interface BulkCreateStagesFromPlaybookResponse {
  message: string;
  pipeline_id: string;
  type: 'lead' | 'client';
  created_count: number;
  tasks_created_count: number | null;
  stages: Array<{
    id: string;
    semantic_id: string;
    name: string;
    order_position: number;
    tasks_created?: number;
  }>;
}

// ============================================================================
// ML INSIGHTS API
// ============================================================================

export const pipelineMLInsightsApi = {
  /**
   * Listar insights ML de pipelines
   */
  async list(params?: PipelineMLInsightListParams): Promise<{ count: number; results: PipelineMLInsight[] }> {
    return await apiClient.get<{ count: number; results: PipelineMLInsight[] }>('/pipelines/ml-insights/', { params });
  },

  /**
   * Obtener insight por ID
   */
  async get(id: string): Promise<PipelineMLInsight> {
    return await apiClient.get<PipelineMLInsight>(`/pipelines/ml-insights/${id}/`);
  },

  /**
   * Marcar insight como revisado
   */
  async review(id: string): Promise<{ status: string; reviewed_at: string }> {
    return await apiClient.post<{ status: string; reviewed_at: string }>(`/pipelines/ml-insights/${id}/review/`);
  },

  /**
   * Implementar recomendación del insight
   */
  async implement(id: string, implementation_notes?: string): Promise<{ status: string; applied_at: string }> {
    return await apiClient.post<{ status: string; applied_at: string }>(`/pipelines/ml-insights/${id}/implement/`, {
      implementation_notes,
    });
  },

  /**
   * Descartar insight
   */
  async dismiss(id: string, reason?: string): Promise<{ status: string }> {
    return await apiClient.post<{ status: string }>(`/pipelines/ml-insights/${id}/dismiss/`, {
      reason,
    });
  },

  /**
   * Generar insights para un pipeline (trigger manual)
   */
  async generate(pipelineId: string): Promise<{ insights_created: number; insights: PipelineMLInsight[] }> {
    return await apiClient.post<{ insights_created: number; insights: PipelineMLInsight[] }>(`/pipelines/${pipelineId}/generate_insights/`);
  },

  /**
   * Calcular probabilidades de stages (trigger manual)
   */
  async calculateProbabilities(pipelineId: string): Promise<{ stages_updated: number; results: any[] }> {
    return await apiClient.post<{ stages_updated: number; results: any[] }>(`/pipelines/${pipelineId}/calculate_probabilities/`);
  },
};

// ============================================================================
// STAGE TEMPLATES API
// ============================================================================

export interface CreateStageFromTemplateRequest {
  pipeline_id: string;
  template_id: string;
  position?: number;
}

export interface CreateStageFromTemplateResponse {
  stage: {
    id: string;
    name: string;
    semantic_id: string;
    semantic_category: string;
    order_position: number;
    description?: string;
    color?: string;
    manual_probability?: number;
    ml_probability?: number;
    ml_enabled?: boolean;
    expected_duration_avg_days?: number;
  };
  automation_flow: {
    id: string;
    name: string;
    context_type: string;
    is_active: boolean;
  };
  nodes_created: number;
  edges_created?: number;
  template_id: string;
  already_existed: boolean;  // ✅ Flag to indicate if stage already existed
}

// ============================================================================
// AUTOMATION NODES API
// ============================================================================

export interface AutomationNode {
  id: string;
  node_id: string;
  automation: string;
  node_type: 'trigger' | 'score' | 'condition' | 'action';
  node_type_display: string;
  config: Record<string, any>;
  position_x: number;
  position_y: number;
  label: string;
  description?: string;
  is_active: boolean;
  company: string;
  created_at: string;
  updated_at: string;
}

export interface AutomationEdge {
  id: string;
  edge_id: string;
  automation: string;
  source_node: string;
  source_node_label: string;
  target_node: string;
  target_node_label: string;
  edge_type: 'default' | 'condition_true' | 'condition_false';
  edge_type_display: string;
  config?: Record<string, any>;
  label?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AutomationTrigger {
  id: string;
  company: string;
  automation: string;
  trigger_type: string;
  trigger_type_display: string;
  config: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StageAutomationResponse {
  automation_flow: {
    id: string;
    name: string;
    description: string;
    context_type: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  } | null;
  nodes: AutomationNode[];
  edges: AutomationEdge[];
  trigger: AutomationTrigger | null;
  /** Multiple flows for stages with multiple automations */
  flows?: Array<{
    id: string;
    name: string;
    nodes: AutomationNode[];
    edges: AutomationEdge[];
  }>;
  /** Total count of automation flows */
  total_flows?: number;
  /** Total count of automation nodes */
  total_nodes?: number;
}

// Pipeline Template Types
export interface ApplyPipelineTemplateRequest {
  template_code: string;
}

export interface ApplyPipelineTemplateResponse {
  stages_created: number;
  stages_skipped: number;
  stages: Array<{
    template_id: string;
    stage_id: string;
    stage_name: string;
    nodes_created: number;
  }>;
  skipped: Array<{
    template_id: string;
    stage_id: string;
    stage_name: string;
  }>;
  canvas_layout: {
    nodes: Array<{
      id: string;
      type: string;
      position: { x: number; y: number };
      data: Record<string, unknown>;
    }>;
    edges: Array<{
      id: string;
      source: string;
      target: string;
      type: string;
    }>;
    viewport: { x: number; y: number; zoom: number };
    version: string;
  };
  template_applied: string;
}

/**
 * Stage Templates API
 */
export const stageTemplatesApi = {
  /**
   * Create stage from template
   * POST /api/pipelines/stages/create_from_template/
   */
  async createFromTemplate(data: CreateStageFromTemplateRequest): Promise<CreateStageFromTemplateResponse> {
    return await apiClient.post<CreateStageFromTemplateResponse>('/pipelines/stages/create_from_template/', data);
  },
};

/**
 * Stage Automation API
 */
export const stageAutomationApi = {
  /**
   * Get automation flow with nodes and edges for a stage
   * GET /api/pipelines/stages/{stage_id}/automation/
   *
   * @param stageId - The stage UUID
   * @param actionId - Optional: Filter by specific action/task UUID
   *                   When provided, only automations for that task are returned
   */
  async getAutomation(stageId: string, actionId?: string): Promise<StageAutomationResponse> {
    const params = actionId ? { action_id: actionId } : undefined;
    return await apiClient.get<StageAutomationResponse>(
      `/pipelines/stages/${stageId}/automation/`,
      { params }
    );
  },
};

// ============================================================================
// TRANSITION CONFIG TYPES
// ============================================================================

export interface ConversionCriterion {
  type: 'quote_accepted' | 'payment_received' | 'document_signed' | 'unit_assigned' | 'manual_approval';
  description: string;
  required: boolean;
  config: Record<string, unknown>;
}

export interface ConversionAction {
  type: 'notify' | 'email' | 'task' | 'webhook';
  description: string;
  config: Record<string, unknown>;
}

export interface TransitionConfigResponse {
  has_transition_stage: boolean;
  stage_id?: string;
  stage_name?: string;
  display_name_source?: string;
  display_name_target?: string;
  conversion_criteria?: ConversionCriterion[];
  data_to_transfer?: string[];
  auto_convert?: boolean;
  actions_on_conversion?: ConversionAction[];
  target_pipeline_id?: string;
  message?: string;
  suggestion?: string;
}

export interface UpdateTransitionConfigRequest {
  conversion_criteria?: ConversionCriterion[];
  actions_on_conversion?: ConversionAction[];
  auto_convert?: boolean;
  data_to_transfer?: string[];
}

export interface CriterionMet {
  type: string;
  description: string;
  met: boolean;
  required: boolean;
  details: string;
}

export interface LeadEligibility {
  id: string;
  lead_number: string;
  name: string;
  email: string;
  has_quote: boolean;
  quote_number: string | null;
  criteria_met: CriterionMet[];
  missing_required: string[];
  missing_optional: string[];
}

export interface ConversionEligibilityResponse {
  closing_stage: {
    id: string;
    name: string;
  };
  summary: {
    total_leads: number;
    ready_to_convert: number;
    pending_criteria: number;
  };
  criteria_config: ConversionCriterion[];
  leads_ready: LeadEligibility[];
  leads_pending: LeadEligibility[];
  error?: string;
  has_transition_stage?: boolean;
}

// ============================================================================
// TRANSITION CONFIG API
// ============================================================================

export const transitionConfigApi = {
  /**
   * Get transition configuration for a pipeline's closing stage
   * GET /api/pipelines/configurations/{id}/transition-config/
   */
  async get(pipelineId: string): Promise<TransitionConfigResponse> {
    return await apiClient.get<TransitionConfigResponse>(
      `/pipelines/configurations/${pipelineId}/transition-config/`
    );
  },

  /**
   * Update transition configuration
   * PATCH /api/pipelines/configurations/{id}/transition-config/
   */
  async update(
    pipelineId: string,
    data: UpdateTransitionConfigRequest
  ): Promise<{ message: string; transition_config: Record<string, unknown> }> {
    return await apiClient.patch(
      `/pipelines/configurations/${pipelineId}/transition-config/`,
      data
    );
  },

  /**
   * Get conversion eligibility for all leads in closing stage
   * GET /api/pipelines/configurations/{id}/conversion-eligibility/
   */
  async getEligibility(pipelineId: string): Promise<ConversionEligibilityResponse> {
    return await apiClient.get<ConversionEligibilityResponse>(
      `/pipelines/configurations/${pipelineId}/conversion-eligibility/`
    );
  },

  /**
   * Get funnel statistics for pipeline stages
   * Returns current counts and historical counts (funnel) per stage
   * GET /api/pipelines/configurations/{id}/stage-funnel-stats/
   */
  async getFunnelStats(pipelineId: string): Promise<PipelineFunnelStatsResponse> {
    return await apiClient.get<PipelineFunnelStatsResponse>(
      `/pipelines/configurations/${pipelineId}/stage-funnel-stats/`
    );
  },
};

// ============================================================================
// STAGE TRANSITIONS API (Regress / Fast-Track)
// ============================================================================

export interface RegressLeadRequest {
  lead_id: string;
  target_stage_id: string;
  reason: string;
}

export interface FastTrackLeadRequest {
  lead_id: string;
  target_stage_id: string;
  reason: string;
}

export interface AllowedTransitionTarget {
  id: string;
  name: string;
  semantic_id: string;
  order_position: number;
  max_fast_track_role?: string;
}

export interface AllowedTransitionsResponse {
  can_regress: boolean;
  regression_targets: AllowedTransitionTarget[];
  can_fast_track: boolean;
  fast_track_targets: AllowedTransitionTarget[];
  is_gate: boolean;
}

export const stageTransitionsApi = {
  /**
   * Regress a lead to a previous stage
   * POST /api/pipelines/history/regress/
   */
  async regressLead(data: RegressLeadRequest) {
    return await apiClient.post('/pipelines/history/regress/', data);
  },

  /**
   * Fast-track a lead to a future stage
   * POST /api/pipelines/history/fast-track/
   */
  async fastTrackLead(data: FastTrackLeadRequest) {
    return await apiClient.post('/pipelines/history/fast-track/', data);
  },

  /**
   * Get allowed regression/fast-track targets for a lead
   * GET /api/pipelines/history/allowed-transitions/?lead_id=uuid
   */
  async getAllowedTransitions(leadId: string): Promise<AllowedTransitionsResponse> {
    return await apiClient.get<AllowedTransitionsResponse>(
      '/pipelines/history/allowed-transitions/',
      { params: { lead_id: leadId } }
    );
  },
};

// ============================================================================
// EXPORTS
// ============================================================================

const pipelinesApi = {
  configurations: pipelinesConfigApi,
  stages: pipelineStagesApi,
  mlInsights: pipelineMLInsightsApi,
  stageTemplates: stageTemplatesApi,
  stageAutomation: stageAutomationApi,
  transitionConfig: transitionConfigApi,
  stageTransitions: stageTransitionsApi,
};

export default pipelinesApi;
